/* eslint-disable prettier/prettier */
import { FunctionComponent, h, render, Fragment } from 'preact';
import { StateUpdater, useContext, useState } from 'preact/hooks';
import { getApi } from '../../../src/ts/libs/getApi';
import { currency } from '../../../src/ts/libs/currency';
import { ProductItemProps, Products } from '../context/Product';
import { useFetch } from '../hooks/useFetch';
import { InfoBlock, ProductListWrap } from '../product-list-wrap/product-list-wrap';

export interface ProductListWindow extends Window {
  isLoggedIn: boolean;
}
declare let window: ProductListWindow;

const BuyNowPopUp: FunctionComponent<{ product: ProductItemProps; show: boolean; setShow: StateUpdater<boolean> }> = ({
  product,
  show,
  setShow,
}) => {
  const [qty, setQuantity] = useState(1);
  const [sellingUnit, setSellingUnit] = useState(product?.alternateSellingUnits?.id);
  const [addedToCart, setAddedToCart] = useState(false);
  const { fetchBasket } = useContext(Products);

  const { fetchData: addToCart, error: addBasketError, loading } = useFetch(`${getApi('addToBasket')}`, {});

  const handleClose = () => {
    setAddedToCart(false);
    setShow(false);
  };

  const handleChangeUnit = (evt) => {
    setSellingUnit(evt.currentTarget.value);
    setAddedToCart(false);
  };

  const handleRemoveOne = () => {
    // set the value first
    setAddedToCart(false);
    setQuantity((c) => {
      const newVal = c === 1 ? 1 : +c - 1;

      return newVal;
    });
  };
  const handleAddOne = () => {
    // set the value first
    setAddedToCart(false);
    setQuantity((c) => {
      const newVal = +c + 1;

      return newVal;
    });
  };
  const handleAddToBasket = async () => {
    // Post off basket update

    await addToCart({
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({
        productCode: product.iqid,
        quantity: qty,
        sellingUnit: sellingUnit,
      }),
    });

    fetchBasket();

    handleClose();

    // call event to notify the mini basket
    const event = new Event('basket-update');
    window.dispatchEvent(event);
  };

  if (!product) {
    return null;
  }

  const currentProduct = product?.alternateSellingUnits?.sellingUnits?.length
    ? product?.alternateSellingUnits?.sellingUnits.find((p) => p.id == sellingUnit)
    : undefined;
  return (
    <div className={`buy-now ${show ? 'is-active' : ''}`} aria-hidden={!show} data-test="product-card-buy-now">
      <div className="buy-now__body">
        <div className="buy-now__content">
          <h4 className="buy-now__heading">{product.name}</h4>

          {product?.alternateSellingUnits?.sellingUnits && product?.alternateSellingUnits?.sellingUnits.length >= 1 && (
            <div className="buy-now__features">
              <h3 className="buy-now__features__heading">Available selling units</h3>
              <ul className="buy-now__list">
                {product?.alternateSellingUnits?.sellingUnits.map((p) => (
                  <li key={p.id}>
                    {p.name}
                    {' - '}
                    {currency(p.price)}
                  </li>
                ))}
              </ul>
            </div>
          )}
          <div className="form-field">
            <div className="quantity-control">
              <button
                type="button"
                className={`button button--muted ${loading ? 'is-loading' : ''}`}
                onClick={handleRemoveOne}
                data-test="product-card-buy-now-qty-minus"
              >
                <span className="button__label sr-only">Minus one from quantity</span>
                <svg aria-hidden="true" width={12} height={4} xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M11.333 3.037c0 .244-.2.444-.444.444H1.11a.446.446 0 01-.444-.444V.963c0-.244.2-.444.444-.444h9.778c.244 0 .444.2.444.444v2.074z"
                    fill="currentColor"
                    fillRule="evenodd"
                  />
                </svg>
              </button>
              <input name="quantity" id="basket-quantity" value={qty} type="number" aria-label="Enter Quanity" />
              <button
                type="button"
                className={`button button--muted ${loading ? 'is-loading' : ''}`}
                onClick={handleAddOne}
                data-test="product-card-buy-now-qty-plus"
              >
                <span className="button__label sr-only">Plus one to quantity</span>
                <svg aria-hidden="true" width={12} height={12} xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M11.333 7.037c0 .244-.2.444-.444.444H7.48v3.408c0 .244-.2.444-.444.444H4.963a.446.446 0 01-.444-.444V7.48H1.11a.446.446 0 01-.444-.444V4.963c0-.244.2-.444.444-.444H4.52V1.11c0-.244.2-.444.444-.444h2.074c.244 0 .444.2.444.444V4.52h3.408c.244 0 .444.2.444.444v2.074z"
                    fill="currentColor"
                    fillRule="evenodd"
                  />
                </svg>
              </button>
            </div>
          </div>
          <div className="form-field">
            <select
              name="sellingUnit"
              id="sellingUnit"
              className="select"
              onChange={handleChangeUnit}
              data-test="product-card-buy-now-change-unit"
            >
              {product?.alternateSellingUnits?.sellingUnits &&
                product?.alternateSellingUnits?.sellingUnits.length >= 1 &&
                product?.alternateSellingUnits?.sellingUnits.map((item) => (
                  <option key={item.id} value={item.id} selected={sellingUnit === item.id}>
                    {item.name}
                  </option>
                ))}
            </select>
          </div>
          {currentProduct && (
            <div className="buy-now__price">
              <div className="buy-now__price__current">
                <strong>{currency(currentProduct.price * qty)}</strong> Ex VAT.
              </div>
            </div>
          )}
        </div>
        <div className="buy-now__actions">
          <button
            type="button"
            className={`button button--primary button--icon-left button--block ${loading ? 'is-loading' : ''}`}
            onClick={handleAddToBasket}
            data-test="product-card-buy-now-add-basket"
          >
            <svg viewBox="0 0 36 32" width={24} height={22} aria-hidden="true" xmlns="http://www.w3.org/2000/svg">
              <path
                d="m39.0075 26.8324375 2.9545625-13c.2133125-.938625-.500125-1.8324375-1.4626875-1.8324375h-24.548875l-.572875-2.800625c-.14275-.6980625-.757-1.199375-1.4695625-1.199375h-6.4080625c-.8284375 0-1.5.6715625-1.5 1.5v1c0 .8284375.6715625 1.5 1.5 1.5h4.3676875l4.3905 21.4646875c-1.050375.6040625-1.7581875 1.7366875-1.7581875 3.0353125 0 1.933 1.567 3.5 3.5 3.5s3.5-1.567 3.5-3.5c0-.979625-.4029375-1.8646875-1.0515-2.5h13.1029375c-.6485.6353125-1.0514375 1.520375-1.0514375 2.5 0 1.933 1.567 3.5 3.5 3.5s3.5-1.567 3.5-3.5c0-1.38575-.8055-2.58325-1.9736875-3.1503125l.3448125-1.51725c.2133125-.938625-.500125-1.8324375-1.4626875-1.8324375h-16.776125l-.4090625-2h18.3215625c.700375 0 1.3075-.484625 1.4626875-1.1675625z"
                fill="currentColor"
                fillRule="evenodd"
                transform="translate(-6 -8)"
              />
            </svg>
            <span className="button__label">{addedToCart ? 'Added to order' : 'Add to order'}</span>
          </button>
          <button
            type="button"
            className="button button--outline button--block button--inverted"
            onClick={handleClose}
            data-test="product-card-buy-now-close"
          >
            <span className="button__label">Cancel</span>
          </button>
        </div>
        <button
          type="button"
          className="button button--transparent buy-now__close"
          aria-label="Close"
          onClick={handleClose}
        >
          <span className="button__label">Close</span>
          <svg aria-hidden="true" width={24} height={24} xmlns="http://www.w3.org/2000/svg">
            <path
              d="M12 0C5.371 0 0 5.371 0 12s5.371 12 12 12 12-5.371 12-12S18.629 0 12 0zm5.884 15.15a.581.581 0 010 .823l-1.916 1.91a.581.581 0 01-.823 0L12 14.71l-3.15 3.174a.581.581 0 01-.823 0l-1.91-1.916a.581.581 0 010-.823L9.29 12 6.116 8.85a.581.581 0 010-.823l1.916-1.916a.581.581 0 01.823 0L12 9.291l3.15-3.175a.581.581 0 01.823 0l1.916 1.916a.581.581 0 010 .823L14.709 12l3.175 3.15z"
              fill="#25252D"
              fillRule="evenodd"
            />
          </svg>
        </button>
      </div>
    </div>
  );
};

const ProductCard: FunctionComponent<{ product: ProductItemProps }> = ({ product }) => {
  const {
    stock,
    price,
    url,
    image,
    name,
    sellingunitname,
    disable,
    poa,
    unallocatedStock,
    dueNextMonth,
    dueNextQuantity,
    webShowStock,
  } = product;
  const [showBuyNow, setShowBuyNow] = useState(false);
  const { isLoggedIn, basketItems } = useContext(Products);

  const handleShowBuyNow = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setShowBuyNow(true);
  };

  const inBasket = basketItems
    ?.map((item) => item.alternateSellingUnits?.sellingUnits)
    .flat()
    .some((item) => item.id === product.iqid);

  const extraUnits = product?.alternateSellingUnits?.sellingUnits?.filter(
    (item) => item.id.toString() !== product.alternateSellingUnits.id.toString()
  );

  // const extraUnitString = extraUnits && extraUnits?.length ? extraUnits?.map(item => item.name).join(', ') : undefined;
  const extraUnitExtas =
    extraUnits && extraUnits?.length ? extraUnits?.map(({ name, price, id }) => ({ name, price, id })) : undefined;

  return (
    <div className="product-card" data-test="product-card">
      <a className="product-card__content" href={url}>
        <div className="product-card__header">
          <picture className="product-card__image">
            {image?.url ? (
              <img src={image?.url} alt={image?.alt} />
            ) : (
              <img src="/assets/img/no-photo-product.png" alt="" />
            )}
          </picture>
          <h3 className="product-card__heading">{name}</h3>

          <div className="product-card__meta">
            {/* Show price on application when POA is true */}

            {poa || stock === 'Available to order' ? (
              <p className="stock-tag  stock-tag--green">Available to order</p>
            ) : (
              ''
            )}

            {!poa && (
              <p>
                {/* Show stock level if above 0 */}
                {webShowStock && unallocatedStock > 0 && (
                  <span
                    className={`stock-tag  stock-tag--${
                      stock === 'Last Few' || stock === 'Last few' ? 'red' : 'green'
                    }`}
                  >
                    {unallocatedStock} in stock
                  </span>
                )}

                {/* Show a divider if both in stock and some coming next month */}
                {!webShowStock && stock === 'In stock' && (<span className="stock-tag  stock-tag--green">In Stock</span>)}
                {webShowStock && unallocatedStock > 0 && dueNextQuantity > 0 && (
                  <span className="stock-tag stock-tag--divider">|</span>
                )}

                {/* Show stock level if above 0 */}
                {webShowStock && dueNextQuantity > 0 && (
                  <span className="stock-tag stock-tag--orange">
                    {dueNextQuantity} due in {dueNextMonth && dueNextMonth}
                  </span>
                )}
              </p>
            )}
          </div>
        </div>
        <div className="product-card__footer">
          {poa ? (
            <p className="product-card__poa">Contact us for a price</p>
          ) : (
            <Fragment>
              <p className="product-card__price">
                <strong className="product-card__price__value">{currency(+price)}</strong>
                <span className="product-card__price__vat"> Ex VAT</span>
              </p>

              {sellingunitname && (
                <p class="product-card__selling-unit">
                  <span class="product-card__selling-unit__value">
                    <strong>per {sellingunitname}</strong>
                  </span>
                  <br />
                  {extraUnitExtas && (
                    <span class="product-card__selling-unit__value">
                      {extraUnitExtas.map(({ name, price, id }) => (
                        <span key={id}>
                          {name} - {currency(+price)}
                          <br />
                        </span>
                      ))}
                    </span>
                  )}
                </p>
              )}

              {isLoggedIn && !disable && (
                <button
                  type="button"
                  className="button button--muted button--icon-left product-card__action"
                  onClick={handleShowBuyNow}
                  data-test="product-card-show-buy-now"
                >
                  <svg viewBox="0 0 36 32" width={24} height={22} aria-hidden="true" xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="m39.0075 26.8324375 2.9545625-13c.2133125-.938625-.500125-1.8324375-1.4626875-1.8324375h-24.548875l-.572875-2.800625c-.14275-.6980625-.757-1.199375-1.4695625-1.199375h-6.4080625c-.8284375 0-1.5.6715625-1.5 1.5v1c0 .8284375.6715625 1.5 1.5 1.5h4.3676875l4.3905 21.4646875c-1.050375.6040625-1.7581875 1.7366875-1.7581875 3.0353125 0 1.933 1.567 3.5 3.5 3.5s3.5-1.567 3.5-3.5c0-.979625-.4029375-1.8646875-1.0515-2.5h13.1029375c-.6485.6353125-1.0514375 1.520375-1.0514375 2.5 0 1.933 1.567 3.5 3.5 3.5s3.5-1.567 3.5-3.5c0-1.38575-.8055-2.58325-1.9736875-3.1503125l.3448125-1.51725c.2133125-.938625-.500125-1.8324375-1.4626875-1.8324375h-16.776125l-.4090625-2h18.3215625c.700375 0 1.3075-.484625 1.4626875-1.1675625z"
                      fill="currentColor"
                      fillRule="evenodd"
                      transform="translate(-6 -8)"
                    />
                  </svg>
                  <span className="button__label">{inBasket ? 'Added' : 'Add'} to order</span>
                </button>
              )}
            </Fragment>
          )}

          <span
            className={`button ${isLoggedIn && !disable ? 'button--outline' : 'button--muted'} product-card__action`}
            data-test="product-card-view-details"
          >
            <span className="button__label  ">View detail</span>
          </span>
        </div>
      </a>
      {isLoggedIn && !disable && <BuyNowPopUp product={product} show={showBuyNow} setShow={setShowBuyNow} />}
    </div>
  );
};

const LoaderCard = () => <div class="product-card-loader"> </div>;

const ProductListContent = () => {
  const { loading, products, error, loadingMore, search } = useContext(Products);
  const loadercardBase = +search?.page || 1;
  const loaderCards = loadercardBase * 8;
  console.log({ loaderCards });
  return (
    <Fragment>
      <div class="product-card-grid" data-test="product-list-grid">
        {(!loading || (loadingMore && loading)) &&
          products &&
          products?.length >= 1 &&
          products.map((item) => <ProductCard key={item.id} product={item} />)}
        {loading && [...Array(loaderCards)].map((_, index) => <LoaderCard key={`loader${index}`} />)}
      </div>
    </Fragment>
  );
};
const ProductList = ({
  categoryId,
  searchPhrase,
  showCategories,
  pagination = 8,
  showAllButton,
  alwaysShowFilters,
  isSearchPage,
  isCategoryPage,
}) => (
  <ProductListWrap
    categoryId={categoryId}
    searchPhrase={searchPhrase}
    showCategories={showCategories}
    pagination={pagination}
    showAllButton={showAllButton}
    alwaysShowFilters={alwaysShowFilters}
    api="productFilters"
    isSearchPage={isSearchPage}
    isCategoryPage={isCategoryPage}
  >
    <ProductListContent />
  </ProductListWrap>
);

try {
  const domContainer = document.querySelector('#product-list');
  if (domContainer) {
    const categoryId = domContainer.getAttribute('data-category');
    const searchPhrase = domContainer.getAttribute('data-search');
    const productListInner = domContainer.querySelector('.product-list');
    const showCategories = !!domContainer.getAttribute('data-show-categories');
    const alwaysShowFilters = !!domContainer.getAttribute('data-force-filters');
    const isSearchPage = !!domContainer.getAttribute('data-is-search-page');
    const isCategoryPage = !!domContainer.getAttribute('data-is-category-page');
    const hideShowAllButton = domContainer.getAttribute('data-hide-all-button');

    render(
      <ProductList
        categoryId={categoryId ? categoryId : ''}
        searchPhrase={searchPhrase ? searchPhrase : ''}
        showCategories={showCategories}
        showAllButton={hideShowAllButton === 'true' ? false : true}
        alwaysShowFilters={alwaysShowFilters}
        isSearchPage={isSearchPage}
        isCategoryPage={isCategoryPage}
      />,
      domContainer,
      productListInner
    );
  }
} catch (e) {
  console.warn(e);
}
